import { Component } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { deleteToken, generateToken, getToken, onError } from 'lib/api';
import thematize from 'lib/thematize';
import { copyTextToClipboard } from 'lib/utils';
import { getErrorHandler, sendSystemMessage } from 'slices/app';
import Button from 'components/Base/Button';
import Icon from 'components/Base/Icon';
import Input from 'components/Base/Input';
import Confirm from './Confirm';
import messages from './messages';
import styles from './Token.scss';

interface State {
  isTokenVisible: boolean;
  token: string;
}

class Token extends Component<unknown, State> {
  state = {
    token: '',
    isTokenVisible: false,
  };

  inputRef: HTMLInputElement | null = null;

  theme = thematize(styles);

  componentDidMount(): void {
    getToken()
      .then(({ payload: { token } }) => this.setState({ token }))
      .catch((e) => onError(e));
  }

  handleInputClick = (): void => {
    // Browsers refuse to copy from a password input, so selecting a masked token is pointless.
    if (this.inputRef && this.state.isTokenVisible) {
      this.inputRef.focus();
      this.inputRef.select();
    }
  };

  handleToggleVisibilityClick = (): void => {
    this.setState(({ isTokenVisible }) => ({ isTokenVisible: !isTokenVisible }));
  };

  handleCopyClick = (): void => {
    copyTextToClipboard(this.state.token, (isCopied) => {
      sendSystemMessage({
        payload: {
          content: (
            <FormattedMessage
              {...messages[isCopied ? 'copied' : 'copyFailed']}
            />
          ),
        },
        error: !isCopied,
      });
    });
  };

  handleGenerateClick = (): void => {
    generateToken()
      .then(({ payload: { token } }) => this.setState({ token }))
      .catch((e) => {
        getErrorHandler({ getError: () => onError(e) })(e);
      });
  };

  handleConfirm = (): void => {
    deleteToken().catch((e) => {
      getErrorHandler({ getError: () => onError(e) })(e);
    });
    this.setState({
      token: '',
      isTokenVisible: false,
    });
  };

  renderToken(): React.ReactElement {
    return (
      <div className={this.theme('token')}>
        <p className={this.theme('token__text')}>
          <FormattedMessage {...messages['tokenLabel']} />
        </p>

        <div className={this.theme('token__field')}>
          <Input
            id="token"
            readOnly
            type={this.state.isTokenVisible ? 'text' : 'password'}
            ref={(ref): void => {
              this.inputRef = ref;
            }}
            containerClassName={this.theme('token__input')}
            className={this.theme('token__token')}
            value={this.state.token}
            onClick={this.handleInputClick}
          />

          <FormattedMessage {...messages[this.state.isTokenVisible ? 'hideAction' : 'showAction']}>
            {(label): React.ReactElement => (
              <Button
                appearance="outlined"
                color="secondary"
                className={this.theme('token__copy')}
                title={String(label)}
                aria-label={String(label)}
                aria-pressed={this.state.isTokenVisible}
                onClick={this.handleToggleVisibilityClick}
              >
                <Icon type={this.state.isTokenVisible ? 'hidden' : 'visible'} />
              </Button>
            )}
          </FormattedMessage>

          <FormattedMessage {...messages['copyAction']}>
            {(label): React.ReactElement => (
              <Button
                appearance="outlined"
                color="secondary"
                className={this.theme('token__copy')}
                title={String(label)}
                aria-label={String(label)}
                onClick={this.handleCopyClick}
              >
                <Icon type="copy" />
              </Button>
            )}
          </FormattedMessage>
        </div>

        <div className={this.theme('token__action')}>
          <Confirm onConfirm={this.handleConfirm} />
        </div>
      </div>
    );
  }

  renderGeneration(): React.ReactElement {
    return (
      <div className={this.theme('token')}>
        <p className={this.theme('token__text')}>
          <FormattedMessage
            {...messages['generateText']}
            values={{
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </p>

        <Button
          color="secondary"
          className={this.theme('token-btn')}
          onClick={this.handleGenerateClick}
        >
          <FormattedMessage {...messages['generateAction']} />
        </Button>
      </div>
    );
  }

  render(): React.ReactElement {
    return (
      <div className={this.theme('container')}>
        <div className={this.theme('description')}>
          <FormattedMessage
            {...messages['description']}
            values={{
              b: (chunks) => <b>{chunks}</b>,
              p: (chunks) => <p>{chunks}</p>,
            }}
          />
        </div>

        {this.state.token ? this.renderToken() : this.renderGeneration()}
      </div>
    );
  }
}

export default Token;
